// Preferences/Settings Type Definitions

export type PreferenceCategory =
  | 'general'
  | 'appearance'
  | 'keyboard'
  | 'projects'
  | 'performance'
  | 'advanced'
  | 'extensions'
  | 'about';

export type StartupBehavior = 'welcome' | 'lastProject' | 'nothing';

export type ThemeMode = 'dark' | 'light' | 'system';

export interface GeneralSettings {
  language: string;
  autoSaveEnabled: boolean;
  autoSaveInterval: number; // seconds
  recentProjectsCount: number;
  startupBehavior: StartupBehavior;
  checkForUpdates: boolean;
  sendAnalytics: boolean;
}

export interface AppearanceSettings {
  theme: ThemeMode;
  accentColor: string;
  fontSize: number;
  fontFamily: string;
  gridOpacity: number;
  gridSize: number;
  showMinimap: boolean;
  showBreadcrumbs: boolean;
  animationsEnabled: boolean;
}

export interface KeyboardShortcut {
  id: string;
  action: string;
  category: string;
  defaultKey: string;
  currentKey: string;
  isCustom: boolean;
}

export interface KeyboardSettings {
  shortcuts: KeyboardShortcut[];
}

export interface ProjectSettings {
  defaultLocation: string;
  defaultTemplate: string;
  defaultScriptLanguage: 'typescript' | 'javascript';
  gitInitByDefault: boolean;
  autoOpenAfterCreate: boolean;
}

export interface PerformanceSettings {
  hardwareAcceleration: boolean;
  maxUndoSteps: number;
  thumbnailCaching: boolean;
  lazyLoading: boolean;
  renderQuality: 'low' | 'medium' | 'high';
  targetFps: number;
}

export interface AdvancedSettings {
  developerMode: boolean;
  experimentalFeatures: boolean;
  logLevel: 'error' | 'warn' | 'info' | 'debug';
  clearCacheOnExit: boolean;
  customDataPath: string;
}

export interface ExtensionInfo {
  id: string;
  name: string;
  version: string;
  author: string;
  description: string;
  enabled: boolean;
  hasSettings: boolean;
}

export interface AllPreferences {
  general: GeneralSettings;
  appearance: AppearanceSettings;
  keyboard: KeyboardSettings;
  projects: ProjectSettings;
  performance: PerformanceSettings;
  advanced: AdvancedSettings;
}

export interface PreferencesState {
  preferences: AllPreferences;
  extensions: ExtensionInfo[];
}

export interface PreferencesActions {
  // Settings
  updateGeneral: (settings: Partial<GeneralSettings>) => void;
  updateAppearance: (settings: Partial<AppearanceSettings>) => void;
  updateKeyboard: (shortcuts: KeyboardShortcut[]) => void;
  updateProjects: (settings: Partial<ProjectSettings>) => void;
  updatePerformance: (settings: Partial<PerformanceSettings>) => void;
  updateAdvanced: (settings: Partial<AdvancedSettings>) => void;

  // Shortcuts
  setShortcut: (id: string, key: string) => void;
  resetShortcut: (id: string) => void;
  resetAllShortcuts: () => void;

  // Extensions
  toggleExtension: (id: string) => void;

  // Apply/Reset
  resetToDefaults: () => void;

  // Import/Export
  exportSettings: () => string;
  importSettings: (data: string) => void;
}

// Default values
export const DEFAULT_GENERAL_SETTINGS: GeneralSettings = {
  language: 'en',
  autoSaveEnabled: true,
  autoSaveInterval: 60,
  recentProjectsCount: 10,
  startupBehavior: 'welcome',
  checkForUpdates: true,
  sendAnalytics: false,
};

export const DEFAULT_APPEARANCE_SETTINGS: AppearanceSettings = {
  theme: 'dark',
  accentColor: '#3b82f6',
  fontSize: 13,
  fontFamily: 'system-ui',
  gridOpacity: 0.5,
  gridSize: 32,
  showMinimap: true,
  showBreadcrumbs: true,
  animationsEnabled: true,
};

export const DEFAULT_PROJECT_SETTINGS: ProjectSettings = {
  defaultLocation: '',
  defaultTemplate: 'empty',
  defaultScriptLanguage: 'typescript',
  gitInitByDefault: false,
  autoOpenAfterCreate: true,
};

export const DEFAULT_PERFORMANCE_SETTINGS: PerformanceSettings = {
  hardwareAcceleration: true,
  maxUndoSteps: 100,
  thumbnailCaching: true,
  lazyLoading: true,
  renderQuality: 'high',
  targetFps: 60,
};

export const DEFAULT_ADVANCED_SETTINGS: AdvancedSettings = {
  developerMode: false,
  experimentalFeatures: false,
  logLevel: 'warn',
  clearCacheOnExit: false,
  customDataPath: '',
};

export const PREFERENCE_CATEGORIES: { id: PreferenceCategory; label: string; icon: string }[] = [
  { id: 'general', label: 'General', icon: 'Settings' },
  { id: 'appearance', label: 'Appearance', icon: 'Palette' },
  { id: 'keyboard', label: 'Keyboard', icon: 'Keyboard' },
  { id: 'projects', label: 'Projects', icon: 'Folder' },
  { id: 'performance', label: 'Performance', icon: 'Gauge' },
  { id: 'advanced', label: 'Advanced', icon: 'Wrench' },
  { id: 'extensions', label: 'Extensions', icon: 'Puzzle' },
  { id: 'about', label: 'About', icon: 'Info' },
];

export const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Espanol' },
  { code: 'fr', name: 'Francais' },
  { code: 'de', name: 'Deutsch' },
  { code: 'ja', name: 'Japanese' },
  { code: 'zh', name: 'Chinese' },
];

export const ACCENT_COLORS = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Cyan', value: '#06b6d4' },
];
